/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import Box from "@mui/material/Box";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import Typography from "@mui/material/Typography";
import * as React from "react";
import LoadDataFile from "../Common/LoadDataFile";
import {
  autoFillChemValues,
  ChemicalDataBaseFields,
  ChemicalField,
  ChemicalFields,
  findDbFieldNames,
  getChemValue,
} from "../../../types/chemicals";
import Button from "@mui/material/Button";
import ProgressButton from "../Common/ProgressButton";
import {
  ISampleRecordCreationPayload,
  ISampleRecordPopulated,
  SpectraDeviceNames,
} from "../../../types/sample";
import CompanyAutoComplete from "../Common/CompanyAutoComplete";
import { useAppContext } from "../../../contexts/useStorageContext";
import { ICompany } from "../../../types/company";
import sampleRecordService from "../../../service/sampleRecord.service";
import { UserRoles } from "../../../types/auth";
import {
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import SampleSourceAutoComplete from "../Common/SampleSourceAutoComplete";
import { ISampleSource } from "../../../types/sampleSource";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import {
  GridRenderEditCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid-pro";
import { serialNumberToDate } from "../../../helpers/formatTime";
import CreateNewSampleGroupDialog from "../SampleRecordsDataTable/CreateNewSampleGroupDialog";
import { getAcetate, getPropionate } from "../../../helpers/gasAnalysisReport";
import usePrevious from "../../../hooks/usePrevious";
import ReportTypeSelect, { ReportTypes } from "../Common/ReportTypeSelect";
import { cloneDeep, uniqBy } from "lodash";
import { useTranslation } from "react-i18next";
import { showSnackbar } from "../../../service/snackbar.service";
import { IAdditiveEffectAlgorithm } from "../../../types/additiveAlgorithm";
import LightTooltip from "../Common/LightTooltip";
import AdditiveEffectElement from "../AdditiveAlgorithm/AdditiveEffectElement";
import { SpectraDevice } from "../Common/SpectraDeviceSelect";

const getNumberValueColumn = (field: ChemicalField) => {
  return {
    field: field.fieldName,
    headerName: field.fieldName,
    width: 100,
    renderCell: (params: any) => {
      const row: ISampleRecordPopulated = params.row;
      const value = getChemValue(row.chemicals, field.fieldName);
      return <div>{value ?? ""}</div>;
    },
  } as any;
};

// Component for uploading and managing chemical analysis data
export default function UploadChemicalAnalysisDataDialog({
  onClose,
  initialItems,
  initialFileName,
  onlyUpdate,
  onSuccess,
  appliedAdditives,
  spectraDevice,
}: {
  onClose: () => void;
  initialItems?: (ISampleRecordCreationPayload & { uid?: number })[];
  initialFileName?: string;
  onlyUpdate?: boolean;
  onSuccess?: () => void;
  appliedAdditives?: IAdditiveEffectAlgorithm[];
  spectraDevice?: SpectraDevice;
}) {
  const { t } = useTranslation();
  const UpdateReasons = {
    ImprovedAnalysis: t("Improved Analysis"),
    Other: t("Other"),
  };
  const { state } = useAppContext(); // Accessing global state
  const { myCompanyProfile, loggedInUserInfo, myClients } = state; // Destructuring needed state properties
  const [errorMessage, setErrorMessage] = React.useState(""); // State for error messages
  const [analysisItems, setAnalysisItems] = React.useState<
    (ISampleRecordCreationPayload & { uid?: number; temporaryId?: number })[]
  >(initialItems?.map((s, i) => ({ ...s, temporaryId: i })) || []); // State for storing analysis items after parsing
  const [saving, setSaving] = React.useState(false); // State to indicate saving process
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const isAdmin =
    loggedInUserInfo?.role &&
    [UserRoles.superadmin, UserRoles.adminStaff].includes(
      loggedInUserInfo?.role as UserRoles
    );
  const [selectedCompany, setSelectedCompany] = React.useState<
    ICompany | undefined
  >(!isAdmin ? myCompanyProfile : undefined);
  const [selectedClient, setSelectedClient] = React.useState<
    ICompany | undefined
  >(
    !isAdmin && myCompanyProfile?.vendorCompanyId ? myCompanyProfile : undefined
  );
  const [existingDataset, setExistingDataset] = React.useState<
    { uid: number; companyUid: number; sampleNumber: string }[]
  >(
    onlyUpdate
      ? initialItems
          ?.filter(
            (i) =>
              i.uid != undefined && i.companyUid != undefined && i.sampleNumber
          )
          .map((i) => ({
            uid: i.uid as number,
            companyUid: i.companyUid as number,
            sampleNumber: i.sampleNumber as string,
          })) || []
      : []
  );
  const [checkingExistence, setCheckingExistence] = React.useState(false);
  const [selectedSampleSource, setSelectedSampleSource] =
    React.useState<ISampleSource>();
  const [fileName, setFileName] = React.useState(initialFileName || "");
  const [editingRow, setEditingRow] = React.useState<ISampleRecordPopulated>();
  const [openGroupCreationDialog, setOpenGroupCreationDialog] =
    React.useState(false);
  const prevStates = usePrevious({ analysisItems });
  const [reason, setReason] = React.useState<string>("");
  const [reasonText, setReasonText] = React.useState<string>("");
  const [validationError, setValidationError] = React.useState<string>("");
  const [initialCoordinatesFetched, setInitialCoordinatesFetched] =
    React.useState(false);
  const [fetchingCoordinates, setFetchingCoordinates] = React.useState(false);

  // const getSingleCoordinates = async (country?: string, region?: string) => {
  //   if (!country) return undefined;
  //   return await getCoordinates(country, region || "");
  // };

  const handleUpdateRow = (
    temporaryId: number,
    updatedRow: ISampleRecordPopulated
  ) => {
    const tempAnalysisItems = [...analysisItems];
    const targetIndex = tempAnalysisItems.findIndex(
      (a) => a.temporaryId == temporaryId
    );
    if (targetIndex < 0) return;
    tempAnalysisItems.splice(targetIndex, 1, updatedRow);
    setAnalysisItems(tempAnalysisItems);
    return updatedRow;
  };

  const TextCellEditElement = (
    params: GridRenderEditCellParams<
      ISampleRecordPopulated,
      any,
      any,
      GridTreeNodeWithRender
    >
  ) => {
    return (
      <TextField
        size="small"
        value={editingRow?.[params.field as keyof ISampleRecordPopulated]}
        onChange={(e) => {
          if (editingRow)
            setEditingRow({
              ...editingRow,
              [params.field as keyof ISampleRecordPopulated]: e.target.value,
            });
        }}
        sx={{
          "& input": {
            py: "6.7px !important",
            px: "4px",
          },
        }}
        inputProps={{
          tabIndex: columns.findIndex(
            (column) => column.field === params.field
          ),
        }}
      />
    );
  };

  const columns = [
    {
      field: "uid",
      headerName: "",
      width: 130,
      renderCell: (params: any) => {
        const row: ISampleRecordPopulated = params.row;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {existingDataset?.some(
              (d) => d.sampleNumber == row.sampleNumber
            ) ? (
              <Tooltip
                title="Sample ID already existing. Will be updated by this."
                placement="right"
                arrow
              >
                <ErrorIcon color="warning" sx={{ mr: 1 }} />
              </Tooltip>
            ) : !!selectedCompany && !checkingExistence ? (
              <Chip
                size="small"
                label="NEW"
                variant="outlined"
                color="success"
              />
            ) : null}

            {existingDataset?.find((d) => d.sampleNumber == row.sampleNumber)
              ?.uid || ""}
          </div>
        );
      },
    },
    {
      field: "companyUid",
      headerName: t("Lab SSID"),
      width: 100,
      renderCell: (params: any) => {
        const row: ISampleRecordCreationPayload = params.row;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {row.companyUid ||
              existingDataset?.find((d) => d.sampleNumber == row.sampleNumber)
                ?.companyUid}
          </div>
        );
      },
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "sampleNumber",
      headerName: t("Sample #"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "sampleSource",
      headerName: t("Farm"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "fieldInformation",
      headerName: t("Field Info"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "sampleTakenLocation",
      headerName: t("Barn (sample taken)"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "pen",
      headerName: t("Pen"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "barn",
      headerName: t("Barn"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "breed",
      headerName: t("Breed"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "rationType",
      headerName: t("Ration Type"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "dataSource",
      headerName: t("Data Source"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "description",
      headerName: t("Description"),
      width: 150,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "bagWeight",
      headerName: t("Bag Weight (mg)"),
      width: 120,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "bagWeight48",
      headerName: `${t("Bag Weight 48 Hr")} (mg)`,
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {params.value ? Number(params.value).toFixed(3) : ""}
            <div style={{ marginLeft: "8px", opacity: 0.5 }}>
              {params.value ? "mg" : ""}
            </div>
          </div>
        );
      },
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "wetSampleWeight",
      headerName: `${t("Wet Sample Weight")} (g)`,
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {params.value ? Number(params.value).toFixed(3) : ""}
            <div style={{ marginLeft: "8px", opacity: 0.5 }}>
              {params.value ? "g" : ""}
            </div>
          </div>
        );
      },
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "drySampleWeight",
      headerName: `${t("Dry Sample Weight")} (g)`,
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {params.value ? Number(params.value).toFixed(3) : ""}
            <div style={{ marginLeft: "8px", opacity: 0.5 }}>
              {params.value ? "g" : ""}
            </div>
          </div>
        );
      },
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "panWeight",
      headerName: `${t("Pan Weight")} (g)`,
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {params.value ? Number(params.value).toFixed(3) : ""}
            <div style={{ marginLeft: "8px", opacity: 0.5 }}>
              {params.value ? "g" : ""}
            </div>
          </div>
        );
      },
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "predictionAiModel",
      headerName: t("Prediction Model"),
      width: 150,
      valueGetter: (params: any) => {
        const row: ISampleRecordPopulated = params.row;
        return [
          ...(row.predictionAiModel ? [row.predictionAiModel] : []),
          ...(row.predictionAiModels || []),
        ]
          .map(
            (model) =>
              `${model.product}${
                model.predictionTarget ? `/${model.predictionTarget}` : ""
              } (${model.date})`
          )
          .join(", ");
      },
    },
    {
      field: "dataset",
      headerName: t("Dataset"),
      width: 70,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "country",
      headerName: t("Country"),
      width: 80,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "region",
      headerName: t("Region"),
      width: 100,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "dietRegion",
      headerName: t("Diet Region"),
      width: 100,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "latitude",
      headerName: t("Latitude"),
      width: 120,
      editable: true,
      renderEditCell: TextCellEditElement,
      renderHeader: (params: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <GpsFixedIcon sx={{ mr: "4px" }} /> {t("Latitude")}
          </div>
        );
      },
    },
    {
      field: "longitude",
      headerName: t("Longitude"),
      width: 120,
      editable: true,
      renderEditCell: TextCellEditElement,
      renderHeader: (params: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <GpsFixedIcon sx={{ mr: "4px" }} /> {t("Longitude")}
          </div>
        );
      },
    },
    {
      field: "year",
      headerName: t("Year"),
      width: 65,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "cropType",
      headerName: t("Crop Type"),
      width: 80,
      editable: true,
      sortable: false,
      renderEditCell: TextCellEditElement,
      valueGetter: (params: any) => {
        const row: ISampleRecordPopulated = params.row;
        return row.type;
      },
    },
    {
      field: "reportType",
      headerName: t("Report Type"),
      width: 130,
      editable: true,
      renderEditCell: (params: any) => {
        return (
          <ReportTypeSelect
            value={editingRow?.reportType as ReportTypes}
            onChange={(newVal) => {
              if (editingRow) {
                setEditingRow({ ...editingRow, reportType: newVal });
              }
            }}
          />
        );
      },
    },
    {
      field: "sampleTakenDate",
      headerName: t("Sampled Date"),
      width: 100,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "product",
      headerName: t("Product"),
      width: 100,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    {
      field: "application",
      headerName: t("Application"),
      width: 100,
      editable: true,
      renderEditCell: TextCellEditElement,
    },
    ...ChemicalFields.map((field) => getNumberValueColumn(field)),
  ];

  const keyColumnFields = new Set([
    "uid",
    "companyUid",
    "sampleNumber",
    "cropType",
  ]);

  const isEmptyValue = (value: unknown) =>
    value === undefined || value === null || value === "";

  const visibleColumns = columns.filter((column) => {
    if (keyColumnFields.has(column.field)) return true;
    const chemField = ChemicalFields.find(
      (field) => field.fieldName === column.field
    );
    if (chemField) {
      return analysisItems.some(
        (row) => !isEmptyValue(getChemValue(row.chemicals, chemField.fieldName))
      );
    }
    if (column.field === "cropType") {
      return analysisItems.some((row) => !isEmptyValue(row.type));
    }
    return analysisItems.some(
      (row) => !isEmptyValue((row as any)[column.field])
    );
  });

  const onParseData = (data: any, fileName?: string) => {
    setFileName(fileName || "");
    const headerFields: string[] = data?.[0]?.map((f: string) => f.trim());
    if (!headerFields?.length) {
      setErrorMessage(t("Header row is empty"));
      return;
    }

    // check required fields are existing
    const missingRequiredFields: string[] = [];
    ChemicalDataBaseFields.map((field) => {
      if (
        field.required &&
        ![field.fieldName, ...(field.aliases || [])].some((f) =>
          headerFields.map((hf) => hf.toLowerCase()).includes(f.toLowerCase())
        )
      ) {
        missingRequiredFields.push(field.fieldName);
      }
    });
    if (missingRequiredFields?.length) {
      setErrorMessage(
        `${t("Required fields are missing")}: ${missingRequiredFields.join(
          ", "
        )}`
      );
      return;
    }

    const parsedChemicalItems: ISampleRecordCreationPayload[] = [];
    const baseFieldsIndexMapper: any = {};
    ChemicalDataBaseFields.map((f) => {
      baseFieldsIndexMapper[f.dbField] = headerFields.findIndex(
        (hf) =>
          hf &&
          [f.fieldName, ...(f.aliases || [])].some(
            (a) => a.toLowerCase() == hf.toLowerCase()
          )
      );
    });

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const chemicalItem: ISampleRecordCreationPayload = {
        temporaryId: i,
        companyUid: row[baseFieldsIndexMapper["companyUid"]],
        sampleNumber: row[baseFieldsIndexMapper["sampleNumber"]]
          ?.toString()
          ?.trim(),
        sampleClient: row[baseFieldsIndexMapper["sampleClient"]]
          ?.toString()
          ?.trim(),
        sampleSource: row[baseFieldsIndexMapper["sampleSource"]]
          ?.toString()
          ?.trim(),
        fieldInformation: row[baseFieldsIndexMapper["fieldInformation"]]
          ?.toString()
          ?.trim(),
        sampleTakenLocation: row[baseFieldsIndexMapper["sampleTakenLocation"]]
          ?.toString()
          ?.trim(),
        pen: row[baseFieldsIndexMapper["pen"]]
          ?.toString()
          ?.trim(),
        barn: row[baseFieldsIndexMapper["barn"]]
          ?.toString()
          ?.trim(),
        breed: row[baseFieldsIndexMapper["breed"]]
          ?.toString()
          ?.trim(),
        rationType: row[baseFieldsIndexMapper["rationType"]]
          ?.toString()
          ?.trim(),
        dataSource: row[baseFieldsIndexMapper["dataSource"]]
          ?.toString()
          ?.trim(),
        bagWeight: row[baseFieldsIndexMapper["bagWeight"]],
        bagWeight48: row[baseFieldsIndexMapper["bagWeight48"]],
        wetSampleWeight: row[baseFieldsIndexMapper["wetSampleWeight"]],
        drySampleWeight: row[baseFieldsIndexMapper["drySampleWeight"]],
        panWeight: row[baseFieldsIndexMapper["panWeight"]],
        dataset: row[baseFieldsIndexMapper["dataset"]],
        reportType: row[baseFieldsIndexMapper["reportType"]],
        country: row[baseFieldsIndexMapper["country"]],
        region: row[baseFieldsIndexMapper["region"]],
        dietRegion: row[baseFieldsIndexMapper["dietRegion"]],
        year: row[baseFieldsIndexMapper["year"]],
        type: row[baseFieldsIndexMapper["type"]],
        description: row[baseFieldsIndexMapper["description"]],
        sampleTakenDate: row[baseFieldsIndexMapper["sampleTakenDate"]]
          ? serialNumberToDate(row[baseFieldsIndexMapper["sampleTakenDate"]])
          : undefined,
        product: row[baseFieldsIndexMapper["product"]],
        application: row[baseFieldsIndexMapper["application"]],
        latitude: row[baseFieldsIndexMapper["latitude"]],
        longitude: row[baseFieldsIndexMapper["longitude"]],
      };
      const chemicals: { [key: string]: number } = {};
      row.map((val: number, j: number) => {
        if (!Object.values(baseFieldsIndexMapper).includes(j)) {
          const dbFieldNames = findDbFieldNames(headerFields[j]);
          if (dbFieldNames?.length) {
            if (!isNaN(val)) {
              dbFieldNames.map((dbFieldName) => {
                chemicals[dbFieldName] = Number(val);
              });
            }
          } else {
            chemicals[headerFields[j]] = Number(val);
          }
        }
      });
      chemicalItem.chemicals = autoFillChemValues(chemicals);
      parsedChemicalItems.push(chemicalItem);
    }
    setAnalysisItems(parsedChemicalItems);
  };

  const saveData = async (groupName: string, groupDescription: string) => {
    if (!selectedCompany?._id && !onlyUpdate) return;
    setSaving(true);
    const result = onlyUpdate
      ? await sampleRecordService.bulkUpdateMultipleRecords(
          analysisItems.map((i) => {
            const cleanedUpItem = cloneDeep(i);
            delete cleanedUpItem.temporaryId;
            return {
              ...cleanedUpItem,
              coordinates:
                !isNaN(Number(i.latitude)) && !isNaN(Number(i.longitude))
                  ? {
                      lat: Number(i.latitude),
                      lon: Number(i.longitude),
                    }
                  : undefined,
            } as ISampleRecordPopulated;
          }),
          reason == UpdateReasons.Other ? reasonText : reason,
          appliedAdditives,
          spectraDevice ? SpectraDeviceNames[spectraDevice] : undefined
        )
      : await sampleRecordService.saveSampleRecords({
          clientId: selectedClient?._id,
          companyId: selectedCompany?._id || "",
          newRecords: analysisItems.map((i) => {
            return {
              ...i,
              coordinates:
                !isNaN(Number(i.latitude)) && !isNaN(Number(i.longitude))
                  ? {
                      lat: Number(i.latitude),
                      lon: Number(i.longitude),
                    }
                  : undefined,
            } as ISampleRecordPopulated;
          }),
          dataField:
            analysisItems?.[0]?.wavelengths?.length &&
            Object.keys(analysisItems?.[0]?.chemicals || {})?.length
              ? "raw"
              : "chemicals",
          fileName,
          groupName,
          groupDescription,
          updateReason: reason == UpdateReasons.Other ? reasonText : reason,
          appliedAdditives,
          spectraDeviceName: spectraDevice ? SpectraDeviceNames[spectraDevice] : undefined
        });
    if (result?.status == 200) {
      setSaveSuccess(true);
      showSnackbar(
        "success",
        "Success!",
        result?.data?.message || t("Saved Successfully")
      );
      onSuccess?.();
    }
    setSaving(false);
  };

  const checkSampleExistence = async () => {
    if (!selectedCompany?._id || !analysisItems?.length) return;
    setCheckingExistence(true);
    const result = await sampleRecordService.checkSampleExistence(
      selectedClient?._id || selectedCompany._id,
      analysisItems.map((a) => a.sampleNumber || ""),
      "chemicals"
    );
    setExistingDataset(result.existingSamples || []);
    let newCompanyUid = result.maxCompanyUid;
    const companyUidAssignedItems = analysisItems.map((item) => {
      if (
        result.existingSamples?.some(
          (s: any) => s.sampleNumber == item.sampleNumber
        )
      ) {
        return item;
      }
      newCompanyUid = newCompanyUid + 1;
      return {
        ...item,
        companyUid: newCompanyUid,
      };
    });
    setAnalysisItems(companyUidAssignedItems);
    setCheckingExistence(false);
  };

  React.useEffect(() => {
    setValidationError("");
  }, [reason, reasonText]);

  React.useEffect(() => {
    if (selectedCompany?._id && analysisItems?.length) {
      checkSampleExistence();
    }
  }, [selectedCompany, selectedClient]);
  React.useEffect(() => {
    if (
      selectedCompany?._id &&
      analysisItems?.length &&
      prevStates?.analysisItems
        ?.map((s) => s.sampleNumber)
        ?.sort()
        ?.join(",") !=
        analysisItems
          ?.map((s) => s.sampleNumber)
          ?.sort()
          ?.join(",")
    ) {
      checkSampleExistence();
    }
  }, [analysisItems]);

  React.useEffect(() => {
    if (analysisItems.length && selectedSampleSource) {
      setAnalysisItems(
        analysisItems.map((a) => ({
          ...a,
          sampleSource: selectedSampleSource.name,
        }))
      );
    }
  }, [selectedSampleSource]);

  return (
    <Dialog open maxWidth={false} onClose={onClose}>
      <div style={{ maxWidth: "100%" }}>
        <Box p={2}>
          <Box pb={2} sx={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <Typography variant="h6">{t("Upload Chem Data")}</Typography>
              {appliedAdditives?.length ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: "warning.main",
                  }}
                >
                  <div
                    style={{
                      marginRight: "4px",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    Applied Additives:
                  </div>
                  {appliedAdditives.map((a) => (
                    <Chip
                      key={a._id}
                      size="small"
                      variant="outlined"
                      label={a.name}
                    />
                  ))}
                </Box>
              ) : null}
            </div>
            <IconButton onClick={onClose} sx={{ marginTop: "-10px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box>
            {errorMessage ? (
              <Box
                sx={{
                  color: "error.main",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  p: 2,
                }}
              >
                {errorMessage}
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => setErrorMessage("")}
                >
                  {t("Try other file")}
                </Button>
              </Box>
            ) : analysisItems.length ? (
              <Box pb={1}>
                <Box
                  sx={{
                    height: "500px",
                    margin: "auto",
                  }}
                >
                  <DataGridPro
                    onRowDoubleClick={(params) => {
                      if (editingRow?.uid != params.id) {
                        setEditingRow(params.row);
                      }
                    }}
                    processRowUpdate={async (newRow, oldRow) => {
                      if (editingRow) {
                        const updatedRow = cloneDeep(editingRow);
                        // if (
                        //   `${editingRow.country}__${editingRow.region}` !=
                        //   `${oldRow.country}__${oldRow.region}`
                        // ) {
                        //   const changedCoordinates = await getSingleCoordinates(
                        //     editingRow.country,
                        //     editingRow.region
                        //   );
                        //   updatedRow.coordinates = changedCoordinates;
                        // }
                        if (
                          `${editingRow.latitude}__${editingRow.longitude}` !=
                          `${oldRow.latitude}__${oldRow.longitude}`
                        ) {
                          updatedRow.coordinates =
                            !isNaN(Number(editingRow.latitude)) &&
                            !isNaN(Number(editingRow.longitude))
                              ? {
                                  lat: Number(editingRow.latitude),
                                  lon: Number(editingRow.longitude),
                                }
                              : undefined;
                        }
                        const updated = await handleUpdateRow(
                          oldRow.temporaryId || -1,
                          updatedRow
                        );
                        setEditingRow(undefined);
                        return updated || newRow;
                      } else {
                        setEditingRow(undefined);
                        return newRow;
                      }
                    }}
                    editMode="row"
                    getRowId={(row) => row.temporaryId || row.companyUid || 0}
                    columns={visibleColumns}
                    rows={analysisItems}
                    rowHeight={38}
                    disableRowSelectionOnClick
                    initialState={{
                      pinnedColumns: {
                        left: ["uid", "companyUid", "sampleNumber", "type"],
                      },
                    }}
                  />
                </Box>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  {checkingExistence ||
                  (!onlyUpdate && !selectedCompany?._id) ||
                  !analysisItems?.length ? (
                    <div style={{ flex: 1 }} />
                  ) : (
                    <div style={{ flex: 1, marginRight: "8px" }}>
                      <div style={{ maxWidth: "470px" }}>
                        {existingDataset?.length ? (
                          <Box sx={{ mt: "-42px" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box
                                sx={{
                                  color: "warning.main",
                                  background: "#ffe3c0",
                                  padding: "10px 9px",
                                  borderRadius: "5px",
                                  mb: 1,
                                }}
                              >{`${existingDataset?.length} samples are already existing and will be updated.`}</Box>
                            </Box>
                            <FormControl>
                              <RadioGroup
                                row
                                name="reason"
                                value={reason}
                                onChange={(e, value) =>
                                  setReason(value as string)
                                }
                              >
                                {Object.values(UpdateReasons).map((reason) => (
                                  <FormControlLabel
                                    key={reason}
                                    value={reason}
                                    control={<Radio />}
                                    label={reason}
                                  />
                                ))}
                              </RadioGroup>
                            </FormControl>
                            {reason == UpdateReasons.Other ? (
                              <TextField
                                sx={{ mt: 1 }}
                                placeholder={t("Reason for update")}
                                value={reasonText}
                                onChange={(e) => setReasonText(e.target.value)}
                                size="small"
                                fullWidth
                              />
                            ) : null}
                            {validationError ? (
                              <Box
                                sx={{
                                  color: "error.main",
                                  mt: 1,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <WarningIcon sx={{ mr: 1 }} color="error" />
                                {validationError}
                              </Box>
                            ) : null}
                          </Box>
                        ) : null}
                        {!existingDataset?.length ? (
                          <Box
                            sx={{
                              color: "success.main",
                              background: "#ceffe5",
                              padding: "10px 9px",
                              borderRadius: "5px",
                            }}
                          >
                            {t("All samples are new records")}
                          </Box>
                        ) : null}
                      </div>
                    </div>
                  )}
                  {onlyUpdate ? null : (
                    <Stack
                      style={{
                        marginRight: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      spacing={1}
                      direction="row"
                    >
                      {!isAdmin ? null : (
                        <CompanyAutoComplete
                          label={t("From Which Lab")}
                          onlyDirectPartners
                          value={selectedCompany}
                          onChange={(selected) =>
                            setSelectedCompany(selected || undefined)
                          }
                        />
                      )}
                      {(isAdmin || myClients?.length) && selectedCompany ? (
                        <CompanyAutoComplete
                          enabled
                          limitedOptions={isAdmin ? undefined : myClients}
                          definedVendor={selectedCompany}
                          label={t("From Which Client")}
                          value={selectedClient}
                          onChange={(selected) =>
                            setSelectedClient(selected || undefined)
                          }
                        />
                      ) : null}
                    </Stack>
                  )}
                  {/* <div style={{ marginRight: "8px", minWidth: "250px" }}>
                    <SampleSourceAutoComplete
                      fullWidth
                      label="Sample Source (Farm)"
                      value={selectedSampleSource}
                      onChange={(selected) =>
                        setSelectedSampleSource(selected || undefined)
                      }
                    />
                  </div> */}

                  {saveSuccess ? (
                    <Box
                      sx={{
                        color: "success.main",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CheckCircleIcon sx={{ mr: 1 }} />
                      {t("Saved Successfully")}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ProgressButton
                        onClick={() => {
                          if (
                            existingDataset?.length &&
                            (!reason ||
                              (reason == UpdateReasons.Other && !reasonText))
                          ) {
                            setValidationError(
                              t("Reason for update is required")
                            );
                          } else {
                            setOpenGroupCreationDialog(true);
                          }
                        }}
                        inProgress={saving}
                        title={t("Save Data")}
                        variant="contained"
                        disableElevation={false}
                        disabled={
                          (!selectedCompany && !onlyUpdate) || checkingExistence
                        }
                      />
                    </Box>
                  )}
                </div>
              </Box>
            ) : (
              <LoadDataFile onParseData={onParseData} />
            )}
          </Box>
        </Box>
      </div>
      {openGroupCreationDialog && (
        <CreateNewSampleGroupDialog
          dialogTitle={t("Save As A Group")}
          recordCount={analysisItems?.length || 0}
          onComplete={(groupName, description) =>
            saveData(groupName, description)
          }
          onClose={() => {
            setOpenGroupCreationDialog(false);
          }}
          cancelButtonTitle={t("Save without grouping")}
        />
      )}
    </Dialog>
  );
}
